import React, { useEffect, useState, useContext } from 'react';
import { Dialog } from '@mui/material';
import FormWysiwyg from '../../shared/form/components/FormWysiwyg';
import { useCmsForm } from '../../shared/form/hooks/useCmsForm';
import { CmsContext } from '../../context/definitions';

const ManageSlideshowsModal = ({
  show,
  onHide,
  moduleColor = 'module-primary',
  pluralCategoriesTitle = 'Slideshows',
  singleCategoryTitle = 'Slideshow',
  slideshows = {},
  onSaveCategory,
  onDeleteCategory,
  onAddCategory,
}) => {
  const { confirm } = useContext(CmsContext);
  const categories = Object.values(slideshows);
  // State for the new category
  const [newCategoryTitle, setNewCategoryTitle] = useState('');

  // State for the category being edited
  const [editableCategory, setEditableCategory] = useState(null);

  // State to track which category edit section is expanded
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);

  const form = useCmsForm(null);

  useEffect(() => {
    categories.forEach((category) => {
      form.setValue(`${category.id}.content`, category?.content || '');
    });
  }, [slideshows]);

  // Handle adding a new category
  const handleAddCategory = () => {
    if (newCategoryTitle.trim()) {
      onAddCategory({ title: newCategoryTitle.trim() });
      setNewCategoryTitle('');
    }
  };

  // Handle editing a category
  const handleEditCategory = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (category) {
      setEditableCategory({ ...category });
      setExpandedCategoryId(categoryId);
    }
  };

  // Handle saving edited category
  const handleSaveCategory = () => {
    if (editableCategory && editableCategory.title.trim()) {
      editableCategory.content = form.getValues(`${editableCategory.id}.content`);
      onSaveCategory(editableCategory);
      setExpandedCategoryId(null);
      setEditableCategory(null);
    }
  };

  // Handle canceling category edit
  const handleCancelCategory = () => {
    setExpandedCategoryId(null);
    setEditableCategory(null);
  };

  // Handle deleting a category
  const handleDeleteCategory = async (categoryId) => {
    onHide();
    const confirmResponse = await confirm({
      title: 'Delete Slideshow',
      message: `Are you sure you want to delete this slideshow?`,
    });
    if (confirmResponse) {
      onDeleteCategory(categoryId);
      if (expandedCategoryId === categoryId) {
        setExpandedCategoryId(null);
        setEditableCategory(null);
      }
    }
  };

  // Sort categories by the specified property
  const sortedCategories = [...categories].sort((a, b) =>
    (a?.title || '').localeCompare(b?.title || '')
  );

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth className="modal-dialog">
      <div className="modal-content">
        <div className={`module ${moduleColor} ng-animate-enabled`}>
          <div className="modal-header module-header">
            <button type="button" className="btn btn-default close" onClick={onHide}>
              <i className="fa fa-times"></i>
            </button>
            <span className="modal-title">Manage {pluralCategoriesTitle}</span>
          </div>
          <div className="modal-body module-body left-margin-12">
            <div id="categoriesModalGrowl" key="categoriesModalGrowl"></div>
            <form name="categoryDialog" role="form" key="categoryDialog">
              {/* Add new category section */}
              <div className="row border-bottom">
                <div className="col-xs-12">
                  <div className="form-horizontal">
                    <div className="form-group">
                      <label htmlFor="newCategoryName" className="col-sm-3 col-xs-12 control-label">
                        Add New {singleCategoryTitle}
                      </label>
                      <div className="col-sm-6 col-xs-9">
                        <input
                          type="text"
                          value={newCategoryTitle}
                          onChange={(e) => setNewCategoryTitle(e.target.value)}
                          className="form-control"
                          name="newCategoryName"
                          id="newCategoryName"
                          placeholder={`${singleCategoryTitle} Name`}
                          required
                        />
                      </div>
                      <div className="col-sm-3 col-xs-3 text-right mt-2">
                        <button
                          id="newCategoryBtn"
                          onClick={handleAddCategory}
                          type="button"
                          className="btn"
                          disabled={!newCategoryTitle.trim()}
                        >
                          Add {singleCategoryTitle}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* List of existing categories */}
              {sortedCategories.map((category, index) => (
                <div
                  key={category.id}
                  className={`row ${index < sortedCategories.length - 1 ? 'border-bottom' : ''}`}
                >
                  <div className="col-xs-12">
                    <div className="form-horizontal">
                      <div className="form-group">
                        <div className="col-sm-10">
                          <label
                            htmlFor={`editCategory${category.id}Btn`}
                            className="control-label"
                            style={{ textAlign: 'left' }}
                          >
                            {category.title}
                          </label>
                          <span style={{ textAlign: 'left' }}> - {category.id}</span>
                        </div>
                        <div className="col-sm-2">
                          <button
                            id={`editCategory${category.id}Btn`}
                            onClick={() => handleEditCategory(category.id)}
                            type="button"
                            className="btn"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapseEdit${category.id}`}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded edit section */}
                  <div
                    className={`col-xs-12 ng-animate-enabled collapse ${
                      expandedCategoryId === category.id ? 'in' : ''
                    }`}
                    style={{
                      marginBottom: index === sortedCategories.length - 1 ? '1em' : 0,
                      borderBottom:
                        index === sortedCategories.length - 1 ? '1px solid #ddd' : 'none',
                    }}
                    id={`collapseEdit${category.id}`}
                  >
                    {editableCategory && expandedCategoryId === category.id && (
                      <div className="form-horizontal">
                        <div className="form-group">
                          <div className="col-sm-2">
                            <label
                              htmlFor={`editCategory${category.id}`}
                              className="control-label"
                              style={{ textAlign: 'left' }}
                            >
                              Title
                            </label>
                          </div>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              className="form-control"
                              value={editableCategory.title}
                              onChange={(e) =>
                                setEditableCategory({ ...editableCategory, title: e.target.value })
                              }
                              id={`editCategory${category.id}`}
                              name={`editCategory${category.id}`}
                              placeholder={category.title}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <div className="col-sm-2">
                            <label
                              htmlFor={`editId${category.id}`}
                              className="control-label"
                              style={{ textAlign: 'left' }}
                            >
                              ID
                            </label>
                          </div>
                          <div className="col-sm-10">
                            <input
                              disabled
                              type="text"
                              className="form-control"
                              value={editableCategory.id}
                              id={`editId${category.id}`}
                              name={`editId${category.id}`}
                              placeholder={category.id}
                            />
                          </div>
                        </div>
                        <div className="form-group padding-left padding-right">
                          <label className="control-label padding-bottom">Message</label>
                          <FormWysiwyg
                            fieldName={`${category.id}.content`}
                            fieldWidth="full"
                            formContext={form}
                            label=""
                            labelWeight={500}
                            tag="textarea"
                          />
                        </div>
                        <div className="form-group">
                          <div className="col-sm-12">
                            <button
                              type="button"
                              onClick={handleSaveCategory}
                              className="btn"
                              disabled={!editableCategory.title.trim()}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(category.id)}
                              className="btn left-margin-9"
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelCategory}
                              className="btn left-margin-9"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapseEdit${category.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </form>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ManageSlideshowsModal;
